import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { blogPosts } from "../data/blogPosts";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=1600";

// pick the first non-empty (trimmed) image; fall back to Unsplash placeholder
const pickImage = (cover = {}) => {
  const first =
    [cover.src, cover.local]
      .map((s) => (typeof s === "string" ? s.trim() : ""))
      .find(Boolean) || "";
  return first || FALLBACK_IMG;
};

const isUnsplash = (url = "") => /images\.unsplash\.com/i.test(url);

// build srcSet only for Unsplash; for local images we omit it
const buildSrcSet = (url) => {
  if (!isUnsplash(url)) return undefined;
  const joiner = url.includes("?") ? "&" : "?";
  return [640, 960, 1280, 1600]
    .map((w) => `${url}${joiner}w=${w} ${w}w`)
    .join(", ");
};

const absoluteUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  try {
    return new URL(u, typeof window !== "undefined" ? window.location.origin : "https://insurancebyag.com").href;
  } catch {
    return u;
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const postIndex = blogPosts.findIndex((p) => p.slug === slug || p.id === slug);
  const post = postIndex >= 0 ? blogPosts[postIndex] : null;

  // 404 fallback
  if (!post) {
    return (
      <>
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 md:px-8 py-16">
          <h1 className="text-2xl font-bold">Post not found</h1>
          <p className="mt-2 text-gray-600">
            The article you’re looking for doesn’t exist.{" "}
            <Link to="/blog" className="text-[#002B5C] font-semibold underline">
              Back to Blog
            </Link>
          </p>
        </main>
        <Footer />
      </>
    );
  }

  // Reading progress
  const [progress, setProgress] = useState(0);
  const articleRef = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      const el = articleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const winH = window.innerHeight || 0;
      const total = el.scrollHeight - winH;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setProgress(total > 0 ? Math.round((scrolled / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Related posts (same category, then fill)
  const related = useMemo(() => {
    const sameCat = blogPosts.filter(
      (p) => p.category === post.category && p.slug !== post.slug
    );
    const others = blogPosts.filter((p) => p.slug !== post.slug);
    return [...sameCat, ...others].slice(0, 3);
  }, [post]);

  // Prev / Next
  const prev = postIndex > 0 ? blogPosts[postIndex - 1] : null;
  const next = postIndex < blogPosts.length - 1 ? blogPosts[postIndex + 1] : null;

  // Social sharing helpers
  const currentUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `https://insurancebyag.com/blog/${post.slug}`;

  const shareTargets = [
    {
      name: "X",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        post.title
      )}&url=${encodeURIComponent(currentUrl)}`,
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        currentUrl
      )}`,
    },
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        currentUrl
      )}`,
    },
  ];

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, url: currentUrl });
      } catch {}
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      alert("Link copied!");
    } catch {}
  };

  // Image state (skeleton + fallback)
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgSrc, setImgSrc] = useState(() => pickImage(post.cover));
  const [imgSrcSet, setImgSrcSet] = useState(() => buildSrcSet(imgSrc));
  useEffect(() => {
    const chosen = pickImage(post.cover);
    setImgSrc(chosen);
    setImgSrcSet(buildSrcSet(chosen));
  }, [post]);

  const title = `${post.title} | Your Choice Associates`;
  const description = post.excerpt;
  const ogImageAbs = absoluteUrl(imgSrc);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:tag" content={post.tags.join(", ")} />
        <meta property="og:image" content={ogImageAbs} />
      </Helmet>

      {/* Reading progress */}
      <div className="sticky top-0 z-30 h-1 bg-transparent">
        <div
          className="h-1 bg-[#002B5C] transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>

      <Navbar />

      <article ref={articleRef} className="px-6 md:px-8">
        {/* Header card */}
        <div className="max-w-3xl mx-auto mt-8">
          <div className="mb-4">
            <button
              onClick={() => navigate(-1)}
              className="text-sm text-[#002B5C] font-semibold hover:underline"
            >
              ← Back
            </button>
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 md:p-8">
            <nav className="text-xs text-gray-500">
              <Link to="/blog" className="hover:underline">
                Blog
              </Link>{" "}
              / <span className="text-gray-700">{post.category}</span>
            </nav>

            <h1 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0b2d52] leading-tight">
              {post.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
              <time>
                {new Date(post.date).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
              <span>•</span>
              <span>{post.readTime} min read</span>
              <span className="inline-flex items-center">
                <span className="mx-2">•</span>
                <span className="inline-block text-[10px] font-semibold tracking-wider uppercase text-[#002B5C] bg-[#e8f1fb] px-2.5 py-1 rounded-full">
                  {post.category}
                </span>
              </span>
            </div>

            {/* Cover image card */}
            <figure className="mt-6 rounded-xl overflow-hidden border border-gray-100 bg-[#fafcff]">
              {!imgLoaded && <div className="animate-pulse h-64 md:h-80 w-full bg-gray-200" />}
              <img
                src={imgSrc}
                alt={post.cover?.alt || post.title}
                width="1600"
                height="900"
                className={`w-full h-64 md:h-80 object-cover ${
                  imgLoaded ? "opacity-100" : "opacity-0"
                } transition-opacity duration-300`}
                loading="lazy"
                srcSet={imgSrcSet}
                sizes={imgSrcSet ? "(max-width: 768px) 100vw, 768px" : undefined}
                onLoad={() => setImgLoaded(true)}
                onError={() => {
                  setImgSrc(FALLBACK_IMG);
                  setImgSrcSet(buildSrcSet(FALLBACK_IMG));
                }}
              />
              <figcaption className="px-4 py-2 text-xs text-gray-500 bg-white">
                Photo: {post.cover?.credit || "—"}
              </figcaption>
            </figure>

            {/* Article body */}
            <div
              className="prose prose-lg max-w-none mt-8 prose-headings:text-[#0b2d52] prose-a:text-[#0b2d52]"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Share row */}
            <div className="mt-10 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600 mr-1">Share:</span>

              <button
                onClick={nativeShare}
                className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
              >
                Quick share
              </button>
              <button
                onClick={copyLink}
                className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
              >
                Copy link
              </button>

              {shareTargets.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                >
                  {s.name}
                </a>
              ))}
            </div>

            {/* Tags */}
            <div className="mt-8 flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <Link
                  key={t}
                  to={`/blog?tag=${encodeURIComponent(t)}`}
                  className="text-xs px-3 py-1 rounded-full bg-[#f1f5fb] text-[#002B5C] font-semibold"
                >
                  #{t}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Prev / Next */}
        <div className="max-w-3xl mx-auto mt-8 grid md:grid-cols-2 gap-4">
          <button
            disabled={!prev}
            onClick={() => prev && navigate(`/blog/${prev.slug}`)}
            className={`text-left rounded-xl border bg-white p-4 md:p-5 transition ${
              prev
                ? "border-gray-100 hover:shadow-sm"
                : "border-gray-100 opacity-50 cursor-not-allowed"
            }`}
          >
            <div className="text-xs text-gray-500">Previous</div>
            <div className="font-semibold text-[#0b2d52] line-clamp-2">
              {prev ? prev.title : "—"}
            </div>
          </button>

          <button
            disabled={!next}
            onClick={() => next && navigate(`/blog/${next.slug}`)}
            className={`text-left rounded-xl border bg-white p-4 md:p-5 transition ${
              next
                ? "border-gray-100 hover:shadow-sm"
                : "border-gray-100 opacity-50 cursor-not-allowed"
            }`}
          >
            <div className="text-xs text-gray-500">Next</div>
            <div className="font-semibold text-[#0b2d52] line-clamp-2">
              {next ? next.title : "—"}
            </div>
          </button>
        </div>

        {/* Related posts */}
        <section className="max-w-6xl mx-auto mt-12 mb-16">
          <h2 className="text-xl font-bold text-[#0b2d52] mb-4 px-1">You might also like</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((r) => {
              const rImg = pickImage(r.cover);
              return (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="bg-white rounded-xl border border-gray-100 hover:shadow-sm transition overflow-hidden"
                >
                  <div className="relative">
                    <img
                      src={rImg}
                      alt={r.cover?.alt || r.title}
                      loading="lazy"
                      width="800"
                      height="450"
                      className="w-full h-40 object-cover"
                      onError={(e) => (e.currentTarget.src = FALLBACK_IMG)}
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block text-[10px] font-semibold tracking-wider uppercase text-[#002B5C] bg-[#e8f1fb] px-2.5 py-1 rounded-full">
                      {r.category}
                    </span>
                    <h3 className="mt-2 font-semibold text-[#0b2d52] line-clamp-2">{r.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mt-1">{r.excerpt}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </article>

      <Footer />
    </>
  );
}
